import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { GUEST_RETENTION_DAYS } from "@fiket/booth-schema";
import { AnimatePresence, motion } from "framer-motion";
import { Camera, RotateCcw } from "lucide-react";
import { useRouter, TOTAL_SHOTS } from "../../store/router";
import { useCameraContext } from "../../camera/CameraProvider";
import { useI18n } from "../../i18n/useI18n";
import { useSelectedStyle, useOutputAspect } from "../../hooks/useStyles";
import { useMediaQuery, PHONE_QUERY } from "../../hooks/useMediaQuery";
import { BackButton } from "../../components/ui/BackButton";
import { Button } from "../../components/ui/Button";
import { FaceGuide } from "../../components/ui/FaceGuide";
import { Lines } from "../../components/ui/Lines";
import { track } from "../../telemetry/activity";

/**
 * idle → counting(카운트다운) → captured(셔터 후 정지 사진 + 확인 — 다시 찍기 / 이대로 진행, BOOTH-56).
 * captured 는 손님이 눈 감음·흔들림을 AI 생성 전에 걸러내는 확인 단계이자, 카메라가 꺼져도(status idle) 화면이
 * 촬영 전 상태로 되돌아가지 않게 하는 안정 단계. 새 Route 를 두지 않아 진행 표시는 "사진 촬영" 그대로.
 */
type Phase = "idle" | "counting" | "captured";

/** 셔터까지의 카운트다운(초). 안내 문구(팁)도 이 값에서 유도된다. */
export const COUNTDOWN_SECONDS = 5;


/**
 * STAGE · Preview(촬영, HERIT 03 · BOOTH-53) — 스테이지 전체 높이의 3:4 슬롯(.capture__slot)에 라이브 카메라를 깔고,
 * 그 위에 종이색 그라데이션 · 타원 얼굴 가이드 · 제목 · 카운트다운 · CTA 를 올린다.
 * 슬롯은 출력 비율 contain 이라 화면에 보이는 프레임 = 촬영 결과(WYSIWYG). 카메라 start/stop 은 App 이 라우트 기준으로
 * 소유하고 여기서는 슬롯 등록·촬영만 담당한다. 폰은 안내 → "카메라 켜기" → 같은 카메라 뷰 두 단계.
 */
export function PreviewView() {
  const go = useRouter((s) => s.go);
  const addShot = useRouter((s) => s.addShot);
  const clearShots = useRouter((s) => s.clearShots);
  const { t } = useI18n();
  const camera = useCameraContext();
  const style = useSelectedStyle();
  const aspect = useOutputAspect();
  const slotRef = useRef<HTMLDivElement | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const [flashKey, setFlashKey] = useState(0);
  // 셔터 직후 뷰포트에 고정할 정지 사진. 라이브 <video> 를 덮어 카메라 정지 시점이 화면에 드러나지 않게 한다.
  const [frozenShot, setFrozenShot] = useState<string | null>(null);

  // 스타일 없이 들어오면 선택으로 되돌림 — 아직 이 화면일 때만(처음으로 뒤 퇴장 애니메이션 중 attract 를 덮어쓰지 않게, 2026-10-21)
  const route = useRouter((s) => s.route);
  useEffect(() => {
    if (route === "preview" && !style) go("styleSelect");
  }, [route, style, go]);

  // 슬롯 등록 — 폰에서는 카메라 뷰가 나중에 마운트되므로 콜백 ref 로 붙었다/떨어졌다를 그대로 전달
  const { setSlot } = camera;
  const bindSlot = useCallback(
    (el: HTMLDivElement | null) => {
      slotRef.current = el;
      setSlot(el);
    },
    [setSlot]
  );

  // 폰: 안내(텍스트) → 카메라 뷰 두 단계. 스트림은 스테이지 진입 시 App 이 미리 켜 두므로 버튼을 누르면 바로 영상이 뜬다.
  const isPhone = useMediaQuery(PHONE_QUERY);
  const [cameraOpen, setCameraOpen] = useState(false);

  // 카운트다운 → 캡처 → generate
  useEffect(() => {
    if (phase !== "counting" || camera.status !== "ready") return;
    let cancelled = false;
    const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

    (async () => {
      clearShots();
      for (let i = 1; i <= TOTAL_SHOTS; i++) {
        setCountdown(COUNTDOWN_SECONDS);
        for (let n = COUNTDOWN_SECONDS - 1; n >= 0; n--) {
          await sleep(1000);
          if (cancelled) return;
          setCountdown(n);
        }
        const dataUrl = camera.capture();
        setFlashKey((k) => k + 1);
        if (dataUrl) {
          addShot({ id: crypto.randomUUID(), index: i, capturedAt: new Date().toISOString(), dataUrl });
          track("capture_taken");
          // 마지막 컷은 정지 사진으로 고정 — 이후 카메라가 꺼져도 슬롯은 이 사진을 유지.
          // phase 변경으로 이 effect 는 정리되고, generate 전환은 확인 화면의 "이대로 진행" 이 맡는다.
          if (i === TOTAL_SHOTS) {
            setFrozenShot(dataUrl);
            setPhase("captured");
            return;
          }
        }
        await sleep(600);
        if (cancelled) return;
      }
      // 캡처 실패(dataUrl null)로 정지 사진이 없을 때만 여기로 온다
      go("generate");
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, camera.status]);

  // 확인 화면: 다시 찍기 = 정지 사진을 걷고 카운트다운부터 재시작(스트림은 켜져 있어 즉시 가능) · 이대로 진행 = generate
  const retake = () => {
    track("capture_retake");
    clearShots();
    setFrozenShot(null);
    setPhase("counting");
  };
  const startCountdown = () => {
    track("capture_countdown");
    setPhase("counting");
  };
  const proceed = () => go("generate");

  // 카운트다운 중 카메라가 끊기면 idle 로. captured 는 제외 — generate 전환 시 App 이 카메라를 끄는데,
  // 그때 idle 로 되돌리면 퇴장 애니메이션 동안 촬영 전 화면(placeholder·버튼)이 다시 나타나 깜빡인다.
  useEffect(() => {
    if (phase === "counting" && camera.status !== "ready") setPhase("idle");
  }, [phase, camera.status]);

  const isCounting = phase === "counting";
  const isCaptured = phase === "captured";
  // 카운트다운·촬영 완료 중에는 뒤로가기·취소 등 조작 UI 를 고정된 레이아웃으로 유지
  const isBusy = isCounting || isCaptured;
  const ready = camera.status === "ready";
  const errorCopy: Record<string, string> = {
    permission_denied: t.preview.errorPermissionDenied,
    no_device: t.preview.errorNoDevice,
    insecure_context: t.preview.errorInsecureContext,
    timeout: t.preview.errorTimeout,
    unknown: t.preview.errorUnknown,
  };
  const errorText =
    camera.status === "error" ? errorCopy[camera.errorKind ?? "unknown"] ?? t.preview.errorUnknown : null;
  const placeholderTitle =
    camera.status === "error"
      ? camera.errorKind === "no_device" ? t.preview.errorTitleNoDevice : t.preview.errorTitle
      : t.preview.placeholderTitle;
  // HTTPS 가 아니면 재시도해도 소용없으니 버튼을 숨긴다
  const canRetry = camera.status === "error" && camera.errorKind !== "insecure_context";

  const arStyle = {
    "--ar": String(aspect.w / aspect.h),
    "--ar-css": `${aspect.w} / ${aspect.h}`,
  } as CSSProperties;

  // 확인 화면 버튼 — 셔터 플래시(0.55s)가 걷힌 뒤 떠오른다. 정지 사진은 촬영 결과(줌·크롭 반영) 그대로
  const confirmActions = (
    <motion.div
      className="capture__actions capture__actions--confirm"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.4 }}
    >
      <Button variant="secondary" onClick={retake}>
        <RotateCcw size={22} aria-hidden />
        {t.preview.confirmRetake}
      </Button>
      <Button onClick={proceed}>{t.preview.confirmProceed}</Button>
    </motion.div>
  );

  // 촬영 · 취소 · 재연결 버튼 묶음
  const shootActions = isCaptured ? confirmActions : (
    <div className="capture__actions">
      {camera.status === "error" ? (
        canRetry ? <Button onClick={() => void camera.start()}>{t.preview.retry}</Button> : null
      ) : isCounting ? null : (
        // 카운트다운 중엔 비활성 버튼을 남기지 않는다(비활성 CTA 가 "고장" 으로 읽힘). 아이콘은 빈 원(글자 O 로 보임) 대신 카메라
        <Button
          disabled={!ready}
          icon={<Camera size={26} strokeWidth={1.8} aria-hidden />}
          onClick={startCountdown}
        >
          {ready ? t.preview.shoot : t.preview.connecting}
        </Button>
      )}
      {isCounting ? (
        <Button variant="text" onClick={() => setPhase("idle")}>{t.preview.cancel}</Button>
      ) : null}
    </div>
  );

  const flash = flashKey > 0 && (
    <motion.div
      key={flashKey}
      initial={{ opacity: 0 }}
      animate={{ opacity: [0, 1, 1, 0] }}
      transition={{ duration: 0.55, times: [0, 0.08, 0.45, 1], ease: "easeOut" }}
      className="fixed inset-0 bg-white"
      style={{ zIndex: 5, pointerEvents: "none" }}
    />
  );

  // ── 폰 · 1단계: 안내만. 카메라 켜기를 누르면 카메라 뷰로
  if (isPhone && !cameraOpen) {
    return (
      <div className="view view--stackable">
        <BackButton label={t.preview.back} onClick={() => go("styleSelect")} />
        <section className="preview-intro">
          <h1><Lines text={t.preview.title} /></h1>
          <ul className="tips">
            <li>{t.preview.tip1}</li>
            <li>{t.preview.tip2(COUNTDOWN_SECONDS)}</li>
            <li>{t.preview.tip3}</li>
          </ul>
          <div className="actions flex flex-col gap-[10px]">
            <Button wide onClick={() => setCameraOpen(true)}>{t.preview.openCamera}</Button>
          </div>
          <span className="note">{t.preview.note}</span>
          {/* 개인정보 안내(BOOTH-28) — 받기 화면과 같은 키 */}
          <small className="note privacy">{t.printShare.privacy(GUEST_RETENTION_DAYS)}</small>
        </section>
      </div>
    );
  }

  // 뒤로가기: 폰 카메라 뷰는 안내로, iPad 는 스타일 선택으로
  const back = isPhone
    ? { label: t.preview.closeCamera, onClick: () => { setPhase("idle"); setCameraOpen(false); } }
    : { label: t.preview.back, onClick: () => go("styleSelect") };

  // ── 카메라 뷰 (iPad · 폰 2단계 공용): 풀블리드 슬롯 + 오버레이
  return (
    <div className="view view--bleed">
      <div className="capture">
        {/* 슬롯 = 출력 비율 contain. CameraProvider 가 이 rect 위에 fixed <video>(z 1) 를 맞춘다 */}
        <div ref={bindSlot} className="capture__slot" style={arStyle}>
          {isCaptured && frozenShot ? (
            <img className="capture__frozen" src={frozenShot} alt="" draggable={false} />
          ) : null}
          {!ready && !isCaptured ? (
            <div className="capture__placeholder" role={camera.status === "error" ? "alert" : undefined}>
              <strong>{placeholderTitle}</strong>
              <p>{camera.status === "error" ? errorText : <Lines text={t.preview.placeholderBody} />}</p>
            </div>
          ) : null}
          {/* 얼굴 가이드는 슬롯(= 촬영 프레임) 기준으로 놓여야 결과 사진의 얼굴 위치와 맞는다 */}
          {ready && !isCaptured ? <FaceGuide className="capture__guide" /> : null}
        </div>

        {/* 오버레이(z 2+) — 종이색 그라데이션으로 헤더·CTA 영역을 띄우고, 문구·버튼을 올린다 */}
        <div className="capture__grad capture__grad--top" aria-hidden />
        <div className="capture__grad capture__grad--bottom" aria-hidden />

        {!isBusy ? <BackButton className="capture__back" label={back.label} onClick={back.onClick} /> : null}
        <h1 className="capture__title"><Lines text={isCaptured ? t.preview.confirmTitle : t.preview.title} /></h1>

        {isCounting ? (
          <div className="countdown" aria-live="assertive">
            <AnimatePresence mode="popLayout">
              <motion.span
                key={countdown}
                initial={{ scale: 0.7, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 1.2, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                {countdown > 0 ? countdown : ""}
              </motion.span>
            </AnimatePresence>
          </div>
        ) : null}

        <div className="capture__cta">
          {isCounting ? <p className="capture__hint">{t.preview.holdStill}</p> : null}
          {isCaptured ? <p className="capture__hint">{t.preview.confirmHint}</p> : null}
          {shootActions}
          {!isBusy ? <small className="note privacy">{t.printShare.privacy(GUEST_RETENTION_DAYS)}</small> : null}
        </div>
      </div>
      {flash}
    </div>
  );
}
